import { apiClient } from "@/services/apiClient";

/**
 * Homologación simplificada ("ingreso simplificado") state machine for a
 * brand-new electronic emisor. The accountant acts as the client tenant;
 * the backend resolves the target company from the acting user's
 * activeTenantId (same as the fiscal/dgi-onboarding-auto calls). Every
 * endpoint here is guarded by AuthGuard.
 */
export type IngresoSimplificadoStatus =
  | "pending"
  | "portal_user_ready"
  | "postulacion_running"
  | "awaiting_confirmation_code"
  | "postulacion_submitted"
  | "awaiting_proveedor_acceptance"
  | "aceptacion_running"
  | "trabajo_accepted"
  | "authorized"
  | "recibido_sent"
  | "awaiting_produccion_role"
  | "completed"
  | "vigente"
  | "failed"
  | "rejected";

export interface IngresoSimplificadoEntity {
  companyId: string;
  status: IngresoSimplificadoStatus;
  roles: string[];
  dgiSolicitudNumero: string | null;
  postulacionSessionId: string | null;
  aceptacionSessionId: string | null;
  authorizedCfeTypes: string[] | null;
  failureReason: string | null;
  postulacionSubmittedAt: string | null;
  trabajoAcceptedAt: string | null;
  authorizedAt: string | null;
  recibidoSentAt: string | null;
  completedAt: string | null;
  /** DGI Fecha Vigencia (YYYY-MM-DD) — the date from which the emisor can invoice. Null until the padrón confirms it. */
  fechaVigencia: string | null;
  /** When the vigencia cron confirmed the RUC in DGI's emisor padrón. */
  vigenteConfirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const BASE = "/fiscal/ingreso-simplificado";

/** GET — current entity for the active tenant, or null if none started yet. */
export async function getIngresoSimplificado(): Promise<IngresoSimplificadoEntity | null> {
  const { data } = await apiClient.get<IngresoSimplificadoEntity | null>(BASE);
  return data ?? null;
}

/**
 * POST /start — creates the DGI e-Factura user with the
 * homologación-simplificada role only and moves the machine to
 * portal_user_ready. Returns the entity plus the automation sessionId.
 */
export async function startIngresoSimplificado(): Promise<{
  status: IngresoSimplificadoEntity;
  sessionId: string;
}> {
  const { data } = await apiClient.post<{
    status: IngresoSimplificadoEntity;
    sessionId: string;
  }>(`${BASE}/start`);
  return data;
}

/**
 * POST /register — register the homologación WITHOUT driving portal-user
 * automation. Used by self-scratch onboarding where the user created the
 * e-Factura DGI user MANUALLY; just ensures the request row so the team picks it
 * up and the wizard parks on the pending screen.
 */
export async function registerIngresoSimplificado(): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${BASE}/register`,
  );
  return data;
}

/** POST /postulacion — kicks off the DGI postulación automation. */
export async function submitPostulacion(): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${BASE}/postulacion`,
  );
  return data;
}

/**
 * POST /confirmation-code — submit the 15-minute "Código de confirmación
 * de postulación" that DGI emails.
 */
export async function submitConfirmationCode(
  code: string,
): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${BASE}/confirmation-code`,
    { code },
  );
  return data;
}

/** POST /accept-trabajo — SALKANTAI (proveedor) accepts the postulación. */
export async function acceptTrabajo(): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${BASE}/accept-trabajo`,
  );
  return data;
}

/** POST /mark-authorized — operator step (becomes automatic later). */
export async function markAuthorized(payload?: {
  authorizedCfeTypes?: string[];
  dgiSolicitudNumero?: string;
}): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${BASE}/mark-authorized`,
    payload ?? {},
  );
  return data;
}

/** POST /mark-recibido — operator step (becomes automatic later). */
export async function markRecibido(): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${BASE}/mark-recibido`,
  );
  return data;
}

/** POST /mark-awaiting-produccion — operator step (becomes automatic later). */
export async function markAwaitingProduccion(): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${BASE}/mark-awaiting-produccion`,
  );
  return data;
}

/** POST /mark-completed — operator step; final state = producción authorized. */
export async function markCompleted(): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${BASE}/mark-completed`,
  );
  return data;
}

export interface PlatformIngresoSimplificadoListItem {
  companyId: string;
  tenantName: string;
  tenantRut: string;
  status: IngresoSimplificadoStatus;
  failureReason: string | null;
  fechaVigencia: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Platform-admin queue (root / root-admin): the homologación-simplificada
 * requests the Fiscalo team works. Defaults to the active queue; pass
 * status="all" to include completed/vigente.
 */
export async function listPlatformIngresoSimplificado(
  status?: string,
): Promise<{ data: PlatformIngresoSimplificadoListItem[]; total: number }> {
  const { data } = await apiClient.get<{
    data: PlatformIngresoSimplificadoListItem[];
    total: number;
  }>("/platform/ingreso-simplificado", {
    params: status ? { status } : undefined,
  });
  return data;
}

export interface PlatformIngresoSimplificadoDetail {
  companyId: string;
  tenantName: string;
  tenantRut: string;
  status: IngresoSimplificadoStatus;
  roles: string[] | null;
  dgiSolicitudNumero: string | null;
  authorizedCfeTypes: string[] | null;
  failureReason: string | null;
  postulacionSubmittedAt: string | null;
  trabajoAcceptedAt: string | null;
  authorizedAt: string | null;
  recibidoSentAt: string | null;
  completedAt: string | null;
  fechaVigencia: string | null;
  vigenteConfirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** DECRYPTED e-Factura DGI portal-user credentials (root/root-admin only). */
  dgiPortalUser: {
    documentType: string;
    documentNumber: string;
    password: string;
    confirmedAt: string | null;
  } | null;
}

const PLATFORM_BASE = "/platform/ingreso-simplificado";

/** Platform-admin detail for one tenant's homologación, incl. decrypted DGI creds. */
export async function getPlatformIngresoSimplificadoDetail(
  companyId: string,
): Promise<PlatformIngresoSimplificadoDetail> {
  const { data } = await apiClient.get<PlatformIngresoSimplificadoDetail>(
    `${PLATFORM_BASE}/${companyId}`,
  );
  return data;
}

// ── Platform-scoped state-machine drivers (root/root-admin), by companyId ──
// Mirror the tenant-scoped drivers above; the operator advances a SPECIFIC
// tenant's homologación from the /homologaciones detail page.

export async function platformStartIngresoSimplificado(
  companyId: string,
): Promise<{ status: IngresoSimplificadoEntity; sessionId: string }> {
  const { data } = await apiClient.post<{
    status: IngresoSimplificadoEntity;
    sessionId: string;
  }>(`${PLATFORM_BASE}/${companyId}/start`);
  return data;
}

/**
 * Create the homologación request row (status `pending`) for a tenant WITHOUT
 * any DGI call, so it enters the /homologaciones queue + driver. This is how a
 * root operator starts a homologación for an arbitrary tenant that never
 * self-registered one. Idempotent. Root/root-admin.
 */
export async function platformEnsureIngresoSimplificado(
  companyId: string,
): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${PLATFORM_BASE}/${companyId}/ensure-request`,
  );
  return data;
}

export async function platformSubmitPostulacion(
  companyId: string,
): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${PLATFORM_BASE}/${companyId}/postulacion`,
  );
  return data;
}

export async function platformSubmitConfirmationCode(
  companyId: string,
  code: string,
): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${PLATFORM_BASE}/${companyId}/confirmation-code`,
    { code },
  );
  return data;
}

export async function platformAcceptTrabajo(
  companyId: string,
): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${PLATFORM_BASE}/${companyId}/accept-trabajo`,
  );
  return data;
}

export async function platformMarkAuthorized(
  companyId: string,
  payload?: { authorizedCfeTypes?: string[]; dgiSolicitudNumero?: string },
): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${PLATFORM_BASE}/${companyId}/mark-authorized`,
    payload ?? {},
  );
  return data;
}

/**
 * Re-send the "add the Producción role" instructions email to the tenant. We
 * can't grant the role ourselves (it needs the tenant's DGI Servicios/Abitab
 * login) — the tenant adds it; the auto-email already fired when the homologación
 * reached awaiting_produccion_role, and this resends it on demand.
 */
export async function platformResendProduccionRoleEmail(
  companyId: string,
): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${PLATFORM_BASE}/${companyId}/add-produccion-role`,
  );
  return data;
}

export async function platformMarkRecibido(
  companyId: string,
): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${PLATFORM_BASE}/${companyId}/mark-recibido`,
  );
  return data;
}

export async function platformMarkAwaitingProduccion(
  companyId: string,
): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${PLATFORM_BASE}/${companyId}/mark-awaiting-produccion`,
  );
  return data;
}

export async function platformMarkCompleted(
  companyId: string,
): Promise<IngresoSimplificadoEntity> {
  const { data } = await apiClient.post<IngresoSimplificadoEntity>(
    `${PLATFORM_BASE}/${companyId}/mark-completed`,
  );
  return data;
}
